import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MapPin, Home, Briefcase, Plus, MoreHorizontal, Navigation, Target, Search, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const ZomatoAddressSelector = ({ 
  onAddressSelect, 
  onClose, 
  savedAddresses = [],
  isOpen = false 
}) => {
  const [currentStep, setCurrentStep] = useState('select'); // 'select', 'add', 'edit'
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '',
    phone: '',
    address: '',
    landmark: '',
    city: 'Jaipur',
    pincode: '',
    addressType: 'home',
    coordinates: null
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showGoogleMap, setShowGoogleMap] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapContainerReady, setMapContainerReady] = useState(false);

  const addressTypes = [
    { id: 'home', label: 'Home', icon: Home, color: '#ff6b35' },
    { id: 'work', label: 'Work', icon: Briefcase, color: '#6b7280' },
    { id: 'other', label: 'Other', icon: MapPin, color: '#6b7280' }
  ];

  // Helper function to generate unique keys
  const generateUniqueKey = (item, index, prefix = 'item') => {
    if (item && item.id) {
      return `${prefix}-${item.id}-${index}`;
    }
    return `${prefix}-${index}`;
  };

  // Callback ref to detect when map container is ready
  const mapContainerRef = (node) => {
    mapRef.current = node;
    if (node && isOpen && !mapContainerReady) {
      console.log('Map container ref callback - container is ready');
      setMapContainerReady(true);
      setTimeout(() => {
        initializeGoogleMaps();
      }, 100);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('select');
      setSelectedAddress(null);
      setSearchQuery('');
      setMapContainerReady(false); // Reset container ready state
    }

    // Cleanup function to destroy map when component unmounts
    return () => {
      if (map) {
        map.remove();
        setMap(null);
        setMarker(null);
      }
    };
  }, [isOpen]);

  const initializeGoogleMaps = () => {
    // Try Google Maps first for professional look
    console.log('Initializing Google Maps (professional)');
    
    // Check if Google Maps API is already loaded
    if (window.google && window.google.maps) {
      initGoogleMap();
    } else {
      // Load Google Maps API script
      loadGoogleMapsScript();
    }
  };

  const loadGoogleMapsScript = () => {
    try {
      // Check if script is already loaded
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        console.log('Google Maps script already exists, waiting for load...');
        // Wait for the script to load
        const checkGoogleMaps = () => {
          if (window.google && window.google.maps) {
            initGoogleMap();
          } else {
            setTimeout(checkGoogleMaps, 100);
          }
        };
        checkGoogleMaps();
        return;
      }

      console.log('Loading Google Maps API script...');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBB7eCnq1g88yxps20vrHfiW3QC07G3LPU&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google Maps API loaded successfully');
        initGoogleMap();
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps API, falling back to OpenStreetMap');
        toast.error('Google Maps failed to load. Using alternative map.');
        initOpenStreetMap();
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error loading Google Maps script:', error);
      initOpenStreetMap();
    }
  };

  const initGoogleMap = () => {
    try {
      // Check if Google Maps API is loaded
      if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded, falling back to OpenStreetMap');
        setMapError(true);
        toast.error('Google Maps not available. Using alternative map.');
        initOpenStreetMap();
        return;
      }

      // Default to Jaipur coordinates
      const defaultLocation = { lat: 26.9147, lng: 75.7004 };
      
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        zoom: 15,
        center: defaultLocation,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Create marker
      const markerInstance = new window.google.maps.Marker({
        position: defaultLocation,
        map: mapInstance,
        draggable: true,
        title: 'Drag to your exact location',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="18" fill="#ff6b35" stroke="white" stroke-width="4"/>
              <circle cx="20" cy="20" r="8" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20)
        }
      });

      // Add click listener to map
      mapInstance.addListener('click', (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        markerInstance.setPosition(event.latLng);
        setSelectedLocation({ lat, lng });
        
        // Reverse geocode to get address
        reverseGeocode(lat, lng);
      });

      // Add drag listener to marker
      markerInstance.addListener('dragend', (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        setSelectedLocation({ lat, lng });
        
        // Reverse geocode to get address
        reverseGeocode(lat, lng);
      });

      setMap(mapInstance);
      setMarker(markerInstance);
      setMapLoaded(true);
      setMapError(false);
      
      toast.success('Map loaded! Click anywhere to place your pin.');
      
    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
      toast.error('Failed to initialize map.');
    }
  };

  const reverseGeocode = (lat, lng) => {
    if (!window.google || !window.google.maps) return;
    
    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat, lng };
    
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;
        
        // Update address form with detected address
        setAddressForm(prev => ({
          ...prev,
          address: address,
          coordinates: { lat, lng }
        }));
        
        toast.success('Address detected from pin location!');
      } else {
        console.error('Reverse geocoding failed:', status);
        // Still update coordinates even if address lookup fails
        setAddressForm(prev => ({
          ...prev,
          coordinates: { lat, lng }
        }));
      }
    });
  };

  const initOpenStreetMap = () => {
    try {
      console.log('initOpenStreetMap called');
      console.log('mapRef.current:', mapRef.current);
      console.log('window.L:', window.L);
      
      // Check if Leaflet is already loaded
      if (window.L) {
        console.log('Leaflet already loaded, creating map');
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          createLeafletMap();
        });
        return;
      }

      // Load Leaflet CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Load Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => {
        console.log('Leaflet loaded successfully');
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          createLeafletMap();
        });
      };
      script.onerror = () => {
        console.error('Failed to load Leaflet');
        setMapError(true);
        toast.error('Failed to load map. Please try again.');
      };
      document.head.appendChild(script);
    } catch (error) {
      console.error('Error initializing OpenStreetMap:', error);
      setMapError(true);
      toast.error('Failed to initialize map.');
    }
  };

  const createLeafletMap = () => {
    try {
      console.log('createLeafletMap called - container should be ready');
      
      if (!mapRef.current) {
        console.error('Map container still not found in createLeafletMap');
        setMapError(true);
        toast.error('Map container not ready. Please try again.');
        return;
      }
      
      console.log('Creating Leaflet map...');
      
      // Clear any existing map
      mapRef.current.innerHTML = '';

      // Initialize Leaflet map
      const mapInstance = window.L.map(mapRef.current).setView([26.9147, 75.7004], 15);
        
        // Add OpenStreetMap tiles
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(mapInstance);

        // Create custom marker icon
        const customIcon = window.L.divIcon({
          className: 'custom-marker',
          html: `
            <div style="
              width: 40px;
              height: 40px;
              background: #ff6b35;
              border: 4px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            ">
              <div style="
                width: 16px;
                height: 16px;
                background: white;
                border-radius: 50%;
              "></div>
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });

        // Add marker
        const markerInstance = window.L.marker([26.9147, 75.7004], { 
          icon: customIcon,
          draggable: true 
        }).addTo(mapInstance);

        // Add click listener to map
        mapInstance.on('click', (e) => {
          const lat = e.latlng.lat;
          const lng = e.latlng.lng;
          
          markerInstance.setLatLng([lat, lng]);
          setSelectedLocation({ lat, lng });
          
          // Simple reverse geocoding using Nominatim
          reverseGeocodeNominatim(lat, lng);
        });

        // Add drag listener to marker
        markerInstance.on('dragend', (e) => {
          const lat = e.target.getLatLng().lat;
          const lng = e.target.getLatLng().lng;
          
          setSelectedLocation({ lat, lng });
          reverseGeocodeNominatim(lat, lng);
        });

        setMap(mapInstance);
        setMarker(markerInstance);
        setMapLoaded(true);
        setMapError(false);
        
        toast.success('Map loaded! Click anywhere to place your pin.');
      
    } catch (error) {
      console.error('Error creating Leaflet map:', error);
      setMapError(true);
      toast.error('Failed to create map.');
    }
  };

  const reverseGeocodeNominatim = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      
      if (data.display_name) {
        setAddressForm(prev => ({
          ...prev,
          address: data.display_name,
          coordinates: { lat, lng }
        }));
        
        toast.success('Address detected from pin location!');
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      // Still update coordinates even if address lookup fails
      setAddressForm(prev => ({
        ...prev,
        coordinates: { lat, lng }
      }));
    }
  };

  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser.');
      return;
    }

    setIsDetectingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // If map is loaded, move marker to current location
          if (map && marker) {
            // Check if it's Google Maps or Leaflet
            if (window.google && window.google.maps) {
              // Google Maps - ensure marker has setPosition method
              if (marker && typeof marker.setPosition === 'function') {
                const newPosition = new window.google.maps.LatLng(lat, lng);
                marker.setPosition(newPosition);
                map.setCenter(newPosition);
                map.setZoom(18);
                reverseGeocode(lat, lng);
              } else {
                console.error('Google Maps marker not properly initialized');
                toast.error('Map marker not ready. Please try again.');
              }
            } else if (window.L) {
              // Leaflet/OpenStreetMap
              if (marker && typeof marker.setLatLng === 'function') {
                marker.setLatLng([lat, lng]);
                map.setView([lat, lng], 18);
                reverseGeocodeNominatim(lat, lng);
              } else {
                console.error('Leaflet marker not properly initialized');
                toast.error('Map marker not ready. Please try again.');
              }
            }
            
            setSelectedLocation({ lat, lng });
          } else {
            // Fallback: just update the form
            setAddressForm(prev => ({
              ...prev,
              coordinates: { lat, lng }
            }));
          }
          
          toast.success('Location detected! Pin moved to your current location.');
        } catch (error) {
          console.error('Error getting location:', error);
          toast.error('Failed to get your location.');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsDetectingLocation(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable.');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out.');
            break;
          default:
            toast.error('An unknown error occurred while detecting location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  const handleUseCurrentLocation = () => {
    if (currentLocation) {
      setAddressForm(prev => ({
        ...prev,
        address: currentLocation.address,
        city: currentLocation.city,
        pincode: currentLocation.pincode,
        coordinates: { lat: currentLocation.lat, lng: currentLocation.lng }
      }));
      setCurrentStep('add');
      toast.success('Current location selected!');
    } else {
      detectCurrentLocation();
    }
  };

  const handleAddNewAddress = () => {
    setAddressForm({
      name: '',
      phone: '',
      address: '',
      landmark: '',
      city: 'Jaipur',
      pincode: '',
      addressType: 'home',
      coordinates: null
    });
    setCurrentStep('add');
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleAddressTypeSelect = (type) => {
    setAddressForm(prev => ({ ...prev, addressType: type }));
  };

  const handleSaveAddress = () => {
    if (!addressForm.address) {
      toast.error('Please enter your address');
      return;
    }

    const formattedAddress = {
      ...addressForm,
      id: Date.now().toString(),
      savedAt: new Date().toISOString()
    };

    onAddressSelect(formattedAddress);
    onClose();
    toast.success('Address saved successfully!');
  };

  const handleSelectSavedAddress = (address) => {
    onAddressSelect(address);
    onClose();
  };

  const filteredAddresses = savedAddresses.filter(address =>
    address.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    address.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f8f9fa',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => {
            if (currentStep === 'select') {
              onClose();
            } else {
              setCurrentStep('select');
            }
          }}
          style={{
            padding: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowLeft size={24} color="#1f2937" />
        </button>
        
        <h1 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#1f2937',
          margin: 0,
          flex: 1
        }}>
          {currentStep === 'select' ? 'Select a location' : 
           currentStep === 'add' ? 'Add Address' : 'Edit Address'}
        </h1>
      </div>

      {currentStep === 'select' && (
        <>
          {/* Search Bar */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search size={20} color="#ff6b35" style={{ position: 'absolute', left: '12px', zIndex: 1 }} />
              <input
                type="text"
                placeholder="Search for area, street name..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 44px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  outline: 'none',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff6b35';
                  e.target.style.backgroundColor = 'white';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#fafafa';
                }}
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            {/* Use Current Location */}
            <div
              onClick={handleUseCurrentLocation}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                cursor: isDetectingLocation ? 'not-allowed' : 'pointer',
                marginBottom: '12px',
                transition: 'all 0.2s ease',
                opacity: isDetectingLocation ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!isDetectingLocation) {
                  e.currentTarget.style.borderColor = '#ff6b35';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDetectingLocation) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                {isDetectingLocation ? (
                  <Loader size={20} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Target size={20} color="#ff6b35" />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#ff6b35',
                  margin: '0 0 4px'
                }}>
                  {isDetectingLocation ? 'Detecting location...' : 'Use current location'}
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  {currentLocation ? currentLocation.address : 'Tap to detect your current location'}
                </p>
              </div>
              {!isDetectingLocation && (
                <ArrowLeft size={20} color="#6b7280" style={{ transform: 'rotate(180deg)' }} />
              )}
            </div>

            {/* Add New Address */}
            <div
              onClick={handleAddNewAddress}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ff6b35'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <Plus size={20} color="#ff6b35" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#ff6b35',
                  margin: 0
                }}>
                  Add Address
                </p>
              </div>
              <ArrowLeft size={20} color="#6b7280" style={{ transform: 'rotate(180deg)' }} />
            </div>
          </div>

          {/* Saved Addresses */}
          {savedAddresses.length > 0 && (
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              flex: 1,
              overflowY: 'auto'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#6b7280',
                margin: '0 0 16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                SAVED ADDRESSES
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {filteredAddresses.map((address, index) => (
                  <div
                    key={generateUniqueKey(address, index, 'address')}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ff6b35'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    onClick={() => handleSelectSavedAddress(address)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {address.addressType === 'home' ? (
                          <Home size={20} color="#6b7280" />
                        ) : address.addressType === 'work' ? (
                          <Briefcase size={20} color="#6b7280" />
                        ) : (
                          <MapPin size={20} color="#6b7280" />
                        )}
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 4px',
                          textTransform: 'capitalize'
                        }}>
                          {address.name}
                        </h4>
                        <p style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          margin: '0 0 4px',
                          lineHeight: '1.4'
                        }}>
                          {address.address}
                        </p>
                        <p style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          margin: 0
                        }}>
                          Phone number: {address.phone}
                        </p>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle edit/delete
                          }}
                          style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#f3f4f6',
                            border: 'none',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <MoreHorizontal size={16} color="#6b7280" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle navigation
                          }}
                          style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#f3f4f6',
                            border: 'none',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <Navigation size={16} color="#6b7280" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {currentStep === 'add' && (
        <>
          {/* Interactive Google Maps */}
          <div style={{
            height: '300px',
            backgroundColor: '#f3f4f6',
            position: 'relative',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid #e5e7eb'
          }}>
            {/* Map Container */}
            <div 
              ref={mapContainerRef}
              style={{ 
                width: '100%', 
                height: '100%', 
                minHeight: '300px',
                borderRadius: '12px',
                backgroundColor: '#f3f4f6',
                position: 'relative',
                display: 'block'
              }}
            />
            
            {/* Fallback UI - only show if map fails */}
            {mapError && (
              /* Fallback Map */
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '16px',
                borderRadius: '12px'
              }}>
                <MapPin size={64} color="#ff6b35" />
                <div style={{ textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px' }}>
                    Map Unavailable
                  </p>
                  <p style={{ fontSize: '14px', margin: 0 }}>
                    Please enter your address manually below
                  </p>
                </div>
                <button
                  onClick={() => {
                    setMapError(false);
                    setMapLoaded(false);
                    initializeGoogleMaps();
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Retry Map
                </button>
              </div>
            )}
            
            {/* Overlay Instructions */}
            <div style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              right: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              zIndex: 1000
            }}>
              📍 Click anywhere on the map to place your pin
            </div>

            {/* Current Location Button */}
            <button
              onClick={detectCurrentLocation}
              disabled={isDetectingLocation}
              style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                width: '48px',
                height: '48px',
                backgroundColor: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDetectingLocation ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                opacity: isDetectingLocation ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isDetectingLocation) {
                  e.currentTarget.style.borderColor = '#ff6b35';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDetectingLocation) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {isDetectingLocation ? (
                <Loader size={20} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Target size={20} color="#ff6b35" />
              )}
            </button>

            {/* Map Loading Indicator */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '20px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 1001,
              opacity: 0,
              transition: 'opacity 0.3s ease'
            }} id="map-loading-indicator">
              <Loader size={24} color="#ff6b35" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                Loading map...
              </span>
            </div>
          </div>

          {/* Address Form */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            flex: 1,
            overflowY: 'auto'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 16px'
            }}>
              Delivery details
            </h3>

            {/* Address Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Full Address *
              </label>
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                padding: '16px',
                border: '2px solid #e5e7eb',
                transition: 'border-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <MapPin size={20} color="#ff6b35" />
                <input
                  type="text"
                  placeholder="Enter your complete address (e.g., House No, Street, Area, City)"
                  value={addressForm.address || ''}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    flex: 1,
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '16px',
                    color: '#1f2937',
                    outline: 'none',
                    fontWeight: '500'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.parentElement.style.borderColor = '#ff6b35';
                    e.currentTarget.parentElement.style.backgroundColor = 'white';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.parentElement.style.borderColor = '#e5e7eb';
                    e.currentTarget.parentElement.style.backgroundColor = '#f8f9fa';
                  }}
                />
              </div>
            </div>

            {/* Address Details */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Address details*
              </label>
              <input
                type="text"
                placeholder="E.g. Floor, Flat no., Tower"
                value={addressForm.landmark}
                onChange={(e) => setAddressForm(prev => ({ ...prev, landmark: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            {/* Receiver Details (Optional) */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 12px'
              }}>
                Receiver details (optional)
              </h4>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                    {addressForm.name.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    type="text"
                    placeholder="Your name (optional)"
                    value={addressForm.name}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 0',
                      border: 'none',
                      backgroundColor: 'transparent',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1f2937',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="tel"
                    placeholder="Phone number (optional)"
                    value={addressForm.phone}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, phone: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '4px 0',
                      border: 'none',
                      backgroundColor: 'transparent',
                      fontSize: '12px',
                      color: '#6b7280',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Save Address As */}
            <div>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 12px'
              }}>
                Save address as
              </h4>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                {addressTypes.map((type, index) => {
                  const IconComponent = type.icon;
                  const isSelected = addressForm.addressType === type.id;
                  
                  return (
                    <button
                      key={generateUniqueKey(type, index, 'type')}
                      onClick={() => handleAddressTypeSelect(type.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: isSelected ? '#fef2f2' : 'white',
                        border: `2px solid ${isSelected ? '#ff6b35' : '#e5e7eb'}`,
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: isSelected ? '#ff6b35' : '#6b7280',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <IconComponent size={16} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div style={{
            backgroundColor: 'white',
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleSaveAddress}
              disabled={!addressForm.address}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: !addressForm.address ? '#d1d5db' : '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: !addressForm.address ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (addressForm.address) {
                  e.currentTarget.style.backgroundColor = '#e55a2b';
                }
              }}
              onMouseLeave={(e) => {
                if (addressForm.address) {
                  e.currentTarget.style.backgroundColor = '#ff6b35';
                }
              }}
            >
              Save address
            </button>
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default ZomatoAddressSelector;
